import { useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useUserProfile } from 'src/context/userProfileContext';
import { useTrackEvents } from 'src/context/userProfileContext/hooks';
import { ComponentSource, ContentType, TryResult } from 'src/utils/datahubDefaultEvent';
import { useMyProductsProgress } from '../../hooks';
import { Card } from '../card';
import { InterestsSlider } from '../interestsSlider';
import { CoursesErrorState } from '../coursesErrorState';
import { CardSkeleton } from '../cardSkeleton';
import { Wrapper } from './courses.styles';
import { CoursesProps, PathParams } from './courses.types';

const Courses = ({ className, active, onSuccess }: CoursesProps) => {
  const { lang } = useParams() as PathParams;
  const { visible, loggedUserUcode } = useUserProfile();
  const { sendReadActivitiesEvent, sendClickAccessClassEvent } = useTrackEvents({
    loggedUserUcode
  });

  const {
    isLoading,
    isError,
    isSuccess,
    error,
    data: products
  } = useMyProductsProgress({ enabled: visible });

  const isValidProducts = useMemo(() => {
    return Array.isArray(products) && products.length > 0;
  }, [products]);

  const generatePathname = ({ slug, id }) => {
    return `/${lang}/club/${slug}/products/${id}`;
  };

  const getCompletedPercentage = (completedPercentage?: number) => {
    return completedPercentage ? Math.round(completedPercentage) : 0;
  };

  useEffect(() => {
    if (isSuccess) {
      onSuccess?.(products?.length);
    }
  }, [isSuccess, products]);

  useEffect(() => {
    if (active && isSuccess && isValidProducts) {
      sendReadActivitiesEvent({
        contentType: ContentType.COURSE,
        itemsCount: Number(products?.length),
        componentSource: ComponentSource.COURSE_LIST,
        result: TryResult.SUCCESSFUL,
        statusCode: 200
      });
    }

    if (active && isError) {
      const failureDescription =
        typeof error === 'object' ? JSON.stringify(error)?.slice(0, 255) : error;

      sendReadActivitiesEvent({
        contentType: ContentType.COURSE,
        itemsCount: 0,
        componentSource: ComponentSource.COURSE_LIST,
        result: TryResult.FAILURE,
        statusCode: error?.statusCode,
        failureDescription
      });
    }
  }, [active, isSuccess, isError]);

  if (isLoading) {
    return (
      <Wrapper className={className}>
        <CardSkeleton progress />
      </Wrapper>
    );
  }

  if (isError || !isValidProducts) {
    return (
      <Wrapper className={className}>
        <CoursesErrorState />
      </Wrapper>
    );
  }

  return (
    <Wrapper className={className}>
      <InterestsSlider>
        {products?.map((product) => (
          <Card
            key={product.id}
            href={generatePathname(product)}
            title={product.name}
            text={product.membershipName}
            progress={getCompletedPercentage(product.completedPercentage)}
            onClick={() => sendClickAccessClassEvent(product.id)}
          />
        ))}
      </InterestsSlider>
    </Wrapper>
  );
};

export default Courses;
